// Survey Archive API client for the Console app.
// Talks directly to sam-dbam (same base as cloudApi).
// Manages a single .samarchive container ZIP file via the File System Access API.

import { getKernelApiBase } from './cloudApi';
import { unzipSync, zipSync, strToU8, strFromU8 } from 'fflate';
import {
    submitSelective,
    fetchServerManifest,
    fetchFileRef,
    adoptLocalArchive,
    openArchiveChannel,
} from './archiveServerApi';

// ---- Internal helpers ----

function getApiBase() {
    return getKernelApiBase().replace(/\/$/, '');
}

function getOrgHeaders() {
    const headers = {};
    try {
        const { getOrgContext } = require('./cloudApi');
        const ctx = getOrgContext();
        if (ctx.orgId) headers['X-Org-ID'] = ctx.orgId;
        if (ctx.userId) headers['X-User-ID'] = ctx.userId;
    } catch (_) { /* swallow */ }
    return headers;
}

/**
 * Extract a usable error message from a non-OK Response.
 * sam-dbam wraps errors as {"error": {"code": "...", "message": "..."}, "meta": {...}}.
 */
async function extractErrorMessage(response, fallback) {
    let errText = '';
    try { errText = await response.text(); } catch (_) { }
    let parsed = {};
    try { parsed = JSON.parse(errText); } catch (_) { }
    const candidates = [
        parsed?.error?.message,
        parsed?.message,
        typeof parsed?.error === 'string' ? parsed.error : null,
        errText,
        response.statusText,
        fallback,
    ];
    const message = candidates.find(v => typeof v === 'string' && v.length > 0) || fallback;
    return { message, parsed };
}

/**
 * Unwrap sam-dbam response format (same as cloudApi's unwrapSamDbamResponse).
 */
function unwrap(resp) {
    if (!resp || typeof resp !== 'object') return resp;
    if (resp.data !== undefined) {
        const inner = resp.data;
        if (inner === null || inner === undefined) return [];
        if (typeof inner === 'object' && !Array.isArray(inner) && Array.isArray(inner.data)) {
            return inner.data;
        }
        return inner;
    }
    return resp;
}

// ---- Survey Archive Server API ----



/**
 * Hash a Blob as lowercase SHA-256 hex digest (WebCrypto).
 */
export async function sha256OfBlob(blob) {
    const buf = await blob.arrayBuffer();
    const hashBuf = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(hashBuf))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
}

/**
 * Fetch entries from the survey_archives table.
 * GET /dbam/v1/deployments/archives[?survey_id=...]
 */
export async function fetchArchives(surveyId) {
    let url = `${getApiBase()}/deployments/archives`;
    if (surveyId) url += `?survey_id=${surveyId}`;
    const response = await fetch(url, { headers: getOrgHeaders() });
    if (!response.ok) {
        const { message } = await extractErrorMessage(response, 'Failed to fetch archives');
        throw new Error(message);
    }
    return unwrap(await response.json());
}

/**
 * Update notes for a survey archive entry.
 * PUT /dbam/v1/deployments/archives/{archiveId}/notes
 */
export async function updateArchiveNotes(archiveId, notes) {
    const url = `${getApiBase()}/deployments/archives/${archiveId}/notes`;
    const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getOrgHeaders() },
        body: JSON.stringify({ notes }),
    });
    if (!response.ok) {
        const { message } = await extractErrorMessage(response, 'Failed to update notes');
        throw new Error(message);
    }
    return unwrap(await response.json());
}

// (restoreSurveyArchive — the legacy whole-archive restore client for
// POST /dbam/v1/surveys/archive-restore — was removed 2026-08-15. It had no
// remaining callers: restoration is the Archive Manager's two-phase selective
// Reload flow, restoreSelectiveArchive below.)

/**
 * C5 selective restore: restore ONLY the selected current-format roots from a
 * .samarchive, preserving IDs. Professional+ only; no feature gate.
 * POST /dbam/v1/workspace/archive/restore-selective
 *
 * @param {File|Blob} file - the .samarchive
 * @param {{survey_ids?:string[], deployment_ids?:string[], recipient_ids?:string[]}} selector
 * @param {{preflight?:boolean}} [opts] - preflight validates + reports collisions without mutating
 * @returns {Promise<object>} the SelectiveRestoreReport (200 ok, or thrown error with .status 409 carrying collisions)
 */
export async function restoreSelectiveArchive(file, selector, opts = {}) {
    const url = `${getApiBase()}/workspace/archive/restore-selective`;
    const formData = new FormData();
    formData.append('archive', file);
    formData.append('selector', JSON.stringify(selector || {}));
    if (opts.preflight) formData.append('preflight', 'true');
    const response = await fetch(url, {
        method: 'POST',
        headers: getOrgHeaders(),
        body: formData,
    });
    if (!response.ok) {
        const { message, parsed } = await extractErrorMessage(response, 'Selective restore failed');
        const error = new Error(message);
        error.status = response.status; // 409 => parsed is the report with .collisions
        error.data = parsed;
        throw error;
    }
    return unwrap(await response.json());
}

// ---- Survey Archive File Handle (File System Access API) ----

let _surveyArchiveDirHandle = null;
// The active archive is now referenced directly by a FileHandle (the user picks
// the .samarchive file itself in Open, or we create it in New). The directory
// handle above is only used transiently by the New flow, which still needs a
// folder to place a new file in.
let _activeFileHandle = null;
let _activeArchiveFileName = 'workspace.samarchive';

const DB_NAME = 'sam-archive';
const STORE = 'handles';
const DIR_KEY = 'survey-archive-dir';
const FILE_KEY = 'survey-archive-file';
const FILE_NAME_KEY = 'sam_survey_archive_file_name';

// The archive "definition" (which folder and which file are active) is
// browser-local state. IndexedDB and localStorage are scoped to the browser
// origin, not to the logged-in account — so without an account discriminator a
// definition created under one account would be read back under any other
// account that later logs in on the same browser. We derive that discriminator
// from the current org context and fold it into both storage keys so each
// account only ever sees its own archive definition.
function getArchiveScopeId() {
    try {
        const { getOrgContext } = require('./cloudApi');
        const ctx = getOrgContext();
        return ctx.orgId || ctx.userId || 'anon';
    } catch (_) {
        return 'anon';
    }
}

function scopedDirKey() {
    return `${DIR_KEY}::${getArchiveScopeId()}`;
}

function scopedFileKey() {
    return `${FILE_KEY}::${getArchiveScopeId()}`;
}

function scopedFileNameKey() {
    return `${FILE_NAME_KEY}::${getArchiveScopeId()}`;
}

// The org id of the account currently signed in. Used to stamp new archives and
// to test whether an existing archive belongs to this account.
function currentOrgId() {
    try {
        const { getOrgContext } = require('./cloudApi');
        return getOrgContext().orgId || null;
    } catch (_) {
        return null;
    }
}

function openDB() {
    return new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, 1);
        req.onupgradeneeded = () => {
            if (!req.result.objectStoreNames.contains(STORE)) {
                req.result.createObjectStore(STORE);
            }
        };
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error);
    });
}

async function saveDirHandleToDB(handle) {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(handle, scopedDirKey());
    return new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
}

async function loadDirHandleFromDB() {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(scopedDirKey());
    return new Promise((res) => { req.onsuccess = () => res(req.result || null); req.onerror = () => res(null); });
}

async function clearDirHandleFromDB() {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(scopedDirKey());
    } catch (_) { }
}

async function saveFileHandleToDB(handle) {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(handle, scopedFileKey());
    return new Promise((res, rej) => { tx.oncomplete = res; tx.onerror = rej; });
}

async function loadFileHandleFromDB() {
    const db = await openDB();
    const tx = db.transaction(STORE, 'readonly');
    const req = tx.objectStore(STORE).get(scopedFileKey());
    return new Promise((res) => { req.onsuccess = () => res(req.result || null); req.onerror = () => res(null); });
}

async function clearFileHandleFromDB() {
    try {
        const db = await openDB();
        const tx = db.transaction(STORE, 'readwrite');
        tx.objectStore(STORE).delete(scopedFileKey());
    } catch (_) { }
}

/**
 * Get the current in-memory directory handle.
 */
export function getSurveyArchiveDirHandle() {
    return _surveyArchiveDirHandle;
}

/**
 * Set (or clear) the archive directory handle. Persists to IndexedDB.
 */
export async function setSurveyArchiveDirHandle(handle) {
    _surveyArchiveDirHandle = handle;
    if (handle) {
        try { await saveDirHandleToDB(handle); } catch (_) { }
    } else {
        await clearDirHandleFromDB();
    }
}

/**
 * Get the active archive filename.
 */
export function getActiveArchiveFileName() {
    return localStorage.getItem(scopedFileNameKey()) || _activeArchiveFileName;
}

/**
 * Set the active archive filename.
 */
export function setActiveArchiveFileName(name) {
    _activeArchiveFileName = name;
    localStorage.setItem(scopedFileNameKey(), name);
}

/**
 * Reset all archive definition state on logout / account switch.
 *
 * Namespacing the storage keys keeps accounts from *reading* each other's
 * definitions, but the module-level handle and filename live in memory for the
 * lifetime of the single-page app. If a user logs out and back in as someone
 * else without a full page reload, those in-memory values would still point at
 * the previous account's archive file. This clears the in-memory state and
 * drops the current scope's persisted keys so nothing carries across the
 * account boundary.
 */
export async function resetArchiveStateOnLogout() {
    stopArchiveChannel(); // no session, no channel
    _surveyArchiveDirHandle = null;
    _activeFileHandle = null;
    _activeArchiveFileName = 'workspace.samarchive';
    try {
        localStorage.removeItem(scopedFileNameKey());
    } catch (_) { /* swallow */ }
    try {
        await clearDirHandleFromDB();
    } catch (_) { /* swallow */ }
    try {
        await clearFileHandleFromDB();
    } catch (_) { /* swallow */ }
}

/**
 * Get the FileHandle for the active archive. Since Open now selects the
 * .samarchive file directly (and New creates it and adopts its handle), the
 * active file is referenced by its own handle rather than resolved through a
 * directory. The legacy `create` argument is accepted for call-site
 * compatibility but is a no-op: the file always exists once a handle is held.
 */
export async function getActiveFileHandle() {
    return _activeFileHandle;
}

/**
 * Adopt a FileHandle as the active archive. Persists the handle (so it survives
 * reloads) and records its name for display and manifest lookups.
 */
export async function setActiveFileHandle(handle) {
    _activeFileHandle = handle || null;
    if (handle) {
        if (handle.name) setActiveArchiveFileName(handle.name);
        try { await saveFileHandleToDB(handle); } catch (_) { }
    } else {
        await clearFileHandleFromDB();
    }
}

/**
 * The in-memory active file handle, if any (no IndexedDB round-trip).
 */
export function getActiveFileHandleRef() {
    return _activeFileHandle;
}

/**
 * Restore the active file handle from IndexedDB (on app load / settings mount).
 * Falls back to null if nothing was persisted for this account scope.
 */
export async function loadActiveFileHandle() {
    if (_activeFileHandle) return _activeFileHandle;
    try {
        const stored = await loadFileHandleFromDB();
        if (stored) {
            _activeFileHandle = stored;
            if (stored.name) _activeArchiveFileName = stored.name;
            return stored;
        }
    } catch (_) { }
    return null;
}

/**
 * Restore the directory handle from IndexedDB (on app load / settings mount).
 */
export async function loadSurveyArchiveDirHandle() {
    if (_surveyArchiveDirHandle) return _surveyArchiveDirHandle;
    try {
        const stored = await loadDirHandleFromDB();
        if (stored) {
            _surveyArchiveDirHandle = stored;
            return stored;
        }
    } catch (_) { }
    return null;
}

// ---- Container ZIP Helpers ----
// The container .samarchive is a flat ZIP:
//   manifest.json              — metadata about contained surveys
//   SurveyName_uuid/           — subdirectory per archived survey
//     (contents from server's per-survey .samarchive ZIP)

const MANIFEST_NAME = 'manifest.json';

/**
 * Create an empty archive manifest.
 */
function createEmptyManifest() {
    return {
        format: 'sam-surveyor-archive',
        version: '1.0',
        created_at: new Date().toISOString(),
        last_updated_at: new Date().toISOString(),
        surveys: [],
    };
}

/**
 * Read the raw bytes from the current archive file.
 * Returns null if the file is empty (newly created) or doesn't exist.
 */
async function readArchiveBytes() {
    const handle = await getActiveFileHandle();
    if (!handle) return null;
    try {
        const file = await handle.getFile();
        if (file.size === 0) return null;
        const buf = await file.arrayBuffer();
        return new Uint8Array(buf);
    } catch (err) {
        console.warn('Could not read archive file:', err);
        return null;
    }
}

/**
 * Write raw bytes to the active archive file.
 *
 * The File System Access API's writable stream already writes to a temporary
 * swap file and atomically replaces the target on close(), so we no longer need
 * the manual temp-then-swap dance the directory-handle model required. We just
 * make sure we still hold write permission on the handle before opening the
 * stream.
 */
async function writeArchiveBytes(data) {
    if (!_activeFileHandle) throw new Error('No active archive file');
    if (_activeFileHandle.queryPermission) {
        const perm = await _activeFileHandle.queryPermission({ mode: 'readwrite' });
        if (perm !== 'granted' && _activeFileHandle.requestPermission) {
            const req = await _activeFileHandle.requestPermission({ mode: 'readwrite' });
            if (req !== 'granted') throw new Error('Write permission to the archive file was denied');
        }
    }
    const writable = await _activeFileHandle.createWritable();
    await writable.write(data);
    await writable.close();
}

/**
 * Does a file with the given name already exist (with content) in the
 * currently selected archive directory? Used to warn before "New" clobbers
 * an existing archive.
 */
export async function archiveFileExists(name) {
    if (!_surveyArchiveDirHandle) return false;
    try {
        const fh = await _surveyArchiveDirHandle.getFileHandle(name);
        const file = await fh.getFile();
        return file.size > 0;
    } catch (_) {
        return false;
    }
}

/**
 * Build the bytes for a brand-new, empty archive: a ZIP containing only a
 * manifest.json that carries the current account's org id. Stamping ownership
 * at creation is what lets the Archive Manager tell whose archive a file is —
 * even before any survey data is written into it — so archives created under a
 * different account can be rejected up front.
 */
function buildEmptyArchiveBytes() {
    const orgId = currentOrgId() || '';
    const manifest = {
        format: 'sam-surveyor-archive',
        version: '1.0',
        created_at: new Date().toISOString(),
        last_updated_at: new Date().toISOString(),
        org_id: orgId,
        owner: { org_id: orgId },
        surveys: [],
    };
    return zipSync({
        [MANIFEST_NAME]: strToU8(JSON.stringify(manifest, null, 2)),
    });
}

/**
 * Create a brand-new, empty archive file in the selected directory and make it
 * the active archive. The file is written with an ownership-stamped manifest so
 * it actually exists on disk and is attributable to this account; the first
 * archive operation fills it with survey content.
 * @param {string} name - the archive filename (caller ensures the extension).
 */
export async function createEmptyArchiveFile(name) {
    if (!_surveyArchiveDirHandle) throw new Error('No archive directory selected');
    setActiveArchiveFileName(name);
    const fh = await _surveyArchiveDirHandle.getFileHandle(name, { create: true });
    const writable = await fh.createWritable();
    await writable.write(buildEmptyArchiveBytes());
    await writable.close();
    return fh;
}

// Ownership classifications for an archive file, from this account's point of
// view: 'owned' (this account's org id), 'foreign' (a different account's), or
// 'unowned' (no org stamp — e.g. a legacy zero-byte file, adoptable).
export const ARCHIVE_OWNED = 'owned';
export const ARCHIVE_FOREIGN = 'foreign';
export const ARCHIVE_UNOWNED = 'unowned';

function classifyOwnership(archiveOrgId) {
    if (!archiveOrgId) return ARCHIVE_UNOWNED;
    const cur = currentOrgId();
    if (!cur) return ARCHIVE_UNOWNED; // can't determine our account; don't block
    return archiveOrgId === cur ? ARCHIVE_OWNED : ARCHIVE_FOREIGN;
}

/**
 * Inspect the raw bytes of a .samarchive without fully decompressing it. Only
 * the small manifest/header is decoded; the (potentially large) SQLite payload
 * is enumerated but skipped. Returns the org id stamped in the archive, whether
 * it carries data, and this account's ownership classification.
 */
function inspectArchiveBytes(bytes) {
    const names = [];
    let entries;
    try {
        entries = unzipSync(bytes, {
            filter: (f) => {
                names.push(f.name);
                return f.name === 'manifest.json' || f.name === 'header.json';
            },
        });
    } catch (_) {
        return { corrupt: true, empty: true, hasData: false, orgId: null, ownership: ARCHIVE_UNOWNED };
    }
    const hasData = names.includes('ops_account.db') || names.includes('sessions.db');
    let orgId = null;
    const mfBytes = entries['manifest.json'] || entries['header.json'];
    if (mfBytes) {
        try {
            const mf = JSON.parse(strFromU8(mfBytes));
            orgId = mf.org_id || (mf.owner && mf.owner.org_id) || null;
        } catch (_) { /* leave orgId null */ }
    }
    return { corrupt: false, empty: !hasData, hasData, orgId, ownership: classifyOwnership(orgId) };
}

/**
 * Inspect a File (or Blob) handle's contents. A zero-byte file is treated as an
 * empty, unowned archive that any account may adopt (it holds no data).
 */
export async function inspectArchiveFile(file) {
    if (!file || file.size === 0) {
        return { corrupt: false, empty: true, hasData: false, orgId: null, ownership: ARCHIVE_UNOWNED };
    }
    const bytes = new Uint8Array(await file.arrayBuffer());
    return inspectArchiveBytes(bytes);
}

/**
 * List every .samarchive in a directory with its ownership classification, so
 * the Open dialog can show the account's own archives as selectable and mark
 * archives belonging to other accounts as off-limits.
 */
export async function listArchivesInDirectory(dirHandle) {
    if (!dirHandle) return [];
    const out = [];
    for await (const [name, handle] of dirHandle.entries()) {
        if (handle.kind !== 'file') continue;
        if (!name.toLowerCase().endsWith('.samarchive')) continue;
        let info;
        let size = 0;
        try {
            const file = await handle.getFile();
            size = file.size;
            info = await inspectArchiveFile(file);
        } catch (_) {
            info = { corrupt: true, empty: true, hasData: false, orgId: null, ownership: ARCHIVE_UNOWNED };
        }
        out.push({ name, size, ...info });
    }
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
}

/**
 * Read the manifest from the current archive file.
 * Returns the manifest object, or an empty manifest if the file is empty/new.
 */
export async function readArchiveManifest() {
    const bytes = await readArchiveBytes();
    if (!bytes) return createEmptyManifest();
    try {
        const entries = unzipSync(bytes);
        if (entries[MANIFEST_NAME]) {
            return JSON.parse(strFromU8(entries[MANIFEST_NAME]));
        }
    } catch (err) {
        console.error('Failed to read archive manifest:', err);
    }
    return createEmptyManifest();
}

/**
 * Sanitize a string for use in a ZIP entry path.
 */
function sanitizeName(name) {
    return (name || 'unknown').replace(/[^A-Za-z0-9_ -]/g, '_').replace(/\s+/g, '_');
}







/**
 * Quick check: does the current archive file contain at least one survey?
 */
export async function archiveHasContent() {
    const manifest = await readArchiveManifest();
    return manifest.surveys.length > 0;
}



// ---- Workspace Archive API (Design Version 4 — the archive server) ----
//
// The canonical singleton lives server-side, owned by the archive server;
// merges are submitted (submitSelective) and serialized there. The console's
// job on this side is purely the local file: detect divergence before a
// submission, and keep the file current by writing back what the server
// announces. The old stage → prepare → confirm client flow is gone.

/**
 * Fetch historical workspace archives from server.
 */
export async function fetchWorkspaceArchives() {
    const url = `${getApiBase()}/workspace/archives`;
    const response = await fetch(url, { headers: getOrgHeaders() });
    if (!response.ok) {
        const { message } = await extractErrorMessage(response, 'Failed to fetch archives');
        throw new Error(message);
    }
    return unwrap(await response.json());
}

/**
 * Update notes for a workspace archive on server.
 */
export async function updateWorkspaceArchiveNotes(archiveId, notes) {
    const url = `${getApiBase()}/workspace/archives/${archiveId}/notes`;
    const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getOrgHeaders() },
        body: JSON.stringify({ notes }),
    });
    if (!response.ok) {
        const { message } = await extractErrorMessage(response, 'Failed to update notes');
        throw new Error(message);
    }
    return unwrap(await response.json());
}

/**
 * PREFLIGHT: analyze a local .samarchive for collisions before restore.
 * Also used to derive manifest for display.
 */
export async function preflightWorkspaceArchive(file) {
    const url = `${getApiBase()}/workspace/archive/preflight`;
    const formData = new FormData();
    formData.append('archive', file);
    const response = await fetch(url, {
        method: 'POST',
        headers: getOrgHeaders(),
        body: formData,
    });
    if (!response.ok) {
        const { message, parsed } = await extractErrorMessage(response, 'Preflight failed');
        const error = new Error(message);
        error.status = response.status;
        error.data = parsed;
        throw error;
    }
    return unwrap(await response.json());
}

/**
 * Serialize the console's own local writes across tabs. Only the console
 * holds the file handle (by design), so this console-origin Web Lock is the
 * entire client-side concurrency story.
 */
function withLocalWriteLock(fn) {
    if (navigator.locks && navigator.locks.request) {
        return navigator.locks.request('singleton-local-write', { mode: 'exclusive' }, fn);
    }
    return fn(); // environment without Web Locks: single-tab semantics
}

/**
 * The local file's identity token: sha256 of its bytes, or "" when the file
 * is absent/empty/data-less (a freshly created archive carries only an
 * ownership-stamped manifest and must look like a clean slate, exactly as a
 * legacy zero-byte file did).
 */
async function localChecksum() {
    const bytes = await readArchiveBytes();
    if (bytes && inspectArchiveBytes(bytes).hasData) {
        return sha256OfBlob(new Blob([bytes]));
    }
    return '';
}

/**
 * Pull the archive server's scratch copy and durable-write it to the open
 * local file. Idempotent by checksum: if the local file already matches,
 * nothing is written. No-op when no file is open (the merge has landed
 * server-side regardless; the manager reconciles on next open).
 *
 * @param {(p: object) => void} [onProgress]
 * @param {string} [announcedChecksum] The checksum the server just announced,
 *   when the caller has one (the session channel's hello/merge event carries
 *   it). Supplying it lets us settle the common "already current" case without
 *   asking for a download link at all — see below.
 */
export async function writeBackFromServer(onProgress, announcedChecksum) {
    return withLocalWriteLock(async () => {
        if (!_activeFileHandle) return { written: false, reason: 'no-file-open' };

        // Compare BEFORE asking for anything. The scratch copy in the staging
        // bucket is deleted after a day, so a request for a download link is
        // the one step here that can fail for an ordinary, expected reason.
        // When the announced checksum already matches the file on disk there
        // is nothing to fetch, and making the request anyway turned a
        // perfectly current archive into an error alert (2026-08-08).
        const local = await localChecksum();
        if (announcedChecksum && local === announcedChecksum) {
            return { written: false, reason: 'already-current', checksum: local };
        }

        const ref = await fetchFileRef();
        if (!ref || !ref.download_ref) return { written: false, reason: 'no-canonical' };

        if (ref.manifest_checksum && local === ref.manifest_checksum) {
            return { written: false, reason: 'already-current', checksum: local };
        }

        if (onProgress) onProgress({ stage: 'downloading', percent: 40 });
        const response = await fetch(ref.download_ref);
        if (!response.ok) throw new Error(`Archive download failed (HTTP ${response.status})`);
        const bytes = new Uint8Array(await response.arrayBuffer());

        if (onProgress) onProgress({ stage: 'verifying', percent: 70 });
        const got = await sha256OfBlob(new Blob([bytes]));
        if (ref.manifest_checksum && got.toLowerCase() !== String(ref.manifest_checksum).toLowerCase()) {
            throw new Error('Merged file failed checksum verification; local file untouched');
        }

        if (onProgress) onProgress({ stage: 'writing', percent: 85 });
        await writeArchiveBytes(bytes);
        return { written: true, checksum: got };
    });
}

/**
 * CONSOLIDATED ARCHIVE ACTION — submit to the archive server, then bring the
 * local file current. This is the primary entry point for contextual archive.
 *
 * Divergence rule: if the open local file's content differs from the scratch
 * copy the server is holding *before* the merge, we do not submit — the merge
 * would build on content the user isn't looking at. That surfaces as a
 * 409-shaped error so archiveSelectiveWithResync can offer the adopt (resync)
 * recovery, same consent gate as before. A local file that is merely *empty*
 * while the server holds content is the same case (a fresh file opened over an
 * old accumulator), and is caught by the same comparison.
 *
 * No scratch copy at all is NOT that case. The staging bucket drops its
 * objects after a day, so most of the time the server is holding nothing —
 * that is the healthy resting state, not a conflict. The file on this machine
 * is the permanent artifact, so it simply becomes the base for this merge: we
 * upload it and carry on. Treating this as a divergence is what produced a
 * spurious "your file and the server copy differ" prompt on every archive
 * after the first day (2026-08-08).
 */
export async function archiveSelective(selector, onProgress) {
    // 1. Divergence check (local vs the server's scratch copy).
    if (onProgress) onProgress({ stage: 'submitting', percent: 5 });
    const local = await localChecksum();
    let server = '';
    let haveScratchCopy = true;
    try {
        const m = await fetchServerManifest();
        // null is a clean 404 NO_ARCHIVE: no scratch copy exists.
        if (m === null) haveScratchCopy = false;
        else server = m?.manifest_checksum || '';
    } catch (_) {
        // A genuine read failure. We cannot tell what the server holds, so
        // fall through to the comparison rather than assume either way.
    }

    if (!haveScratchCopy) {
        // Nothing to diverge from. Make the local file the merge base. Skipped
        // when the local file has no data — then both sides are empty and the
        // merge starts clean on its own.
        if (local) {
            if (onProgress) onProgress({ stage: 'uploading base', percent: 10 });
            await resyncWorkspaceArchive();
        }
    } else if (local !== server) {
        const err = new Error('Local archive file and server copy differ');
        err.status = 409;
        err.code = 'LOCAL_DIVERGED';
        throw err;
    }

    // 2. Submit. The archive server serializes and merges; this resolves when
    //    the merge has landed on the scratch copy.
    if (onProgress) onProgress({ stage: 'merging', percent: 25 });
    const result = await submitSelective(selector);

    // 3. Bring the local file current. (The channel writer would also catch
    //    this, but the submitting console writes inline so the user's action
    //    completes with the file in hand.)
    const wb = await writeBackFromServer(onProgress, result.manifest_checksum);

    if (onProgress) onProgress({ stage: 'done', percent: 100 });
    return { checksum: result.manifest_checksum, written: wb.written };
}

/**
 * RESYNC: adopt the current local archive file as the canonical singleton on
 * the archive server. Recovery for a divergence (409 LOCAL_DIVERGED): the
 * canonical copy differs from the local file (an interrupted write-back, or
 * the user opened a different archive file). The local file — the copy the
 * user actually holds — is authoritative when they say so.
 */
export async function resyncWorkspaceArchive() {
    const bytes = await readArchiveBytes();
    return adoptLocalArchive(bytes, getActiveArchiveFileName());
}

/**
 * archiveSelective with a user-gated recovery path for the 409
 * CHECKSUM_MISMATCH deadlock. `confirmResync` is supplied by the caller and
 * must show a styled confirmation dialog, resolving true to proceed. On
 * consent the local file is resynced to the server and the archive is retried
 * once; on decline (or any other error) the original error is rethrown.
 */
export async function archiveSelectiveWithResync(selector, onProgress, confirmResync) {
    try {
        return await archiveSelective(selector, onProgress);
    } catch (err) {
        const mismatch = err?.status === 409;
        if (!mismatch || typeof confirmResync !== 'function') throw err;
        const ok = await confirmResync();
        if (!ok) throw err;
        if (onProgress) onProgress({ stage: 'resyncing', percent: 5 });
        await resyncWorkspaceArchive();
        return await archiveSelective(selector, onProgress);
    }
}

/**
 * Contextual archive with the author-consent overwrite gate (archiver UX §A4,
 * coding plan §4g, archive_design.md §1a).
 *
 * Before staging, read the open archive's manifest and find which selected
 * artifacts already exist inside it. If any do, ask the caller to confirm the
 * replace via one in-app dialog (never window.confirm). The server always
 * overwrites the artifact in place — whole-instance, delete-then-insert by
 * primary key — so this is purely the consent gate; it warns the author that a
 * replace cannot be undone except by retrieving a prior archive file.
 *
 * Consent is remembered in sessionStorage, keyed by the archive's current
 * checksum plus the sorted selection, so a retry of a batch that failed at a
 * later stage does not re-prompt for artifacts the author already approved. The
 * entry is cleared once the archive finally succeeds, so a genuinely new
 * re-archive of the same artifact prompts again.
 *
 * @param {object}   selector    - { survey_ids?, recipient_ids?, deployment_ids? }
 * @param {Function} onProgress
 * @param {object}   opts        - { confirmOverwrite(collisions, filename)->bool, confirmResync()->bool }
 * @returns {Promise<object>} the archiveSelective result
 */
export async function archiveWithOverwriteCheck(selector, onProgress, opts = {}) {
    const { confirmOverwrite, confirmResync } = opts;

    // 1. Read the canonical singleton's manifest (from the archive server) to
    //    see what the archive already contains. No archive yet, or an
    //    unreadable manifest, simply yields no collisions.
    let manifest = null;
    let serverChecksum = '';
    try {
        const res = await fetchServerManifest();
        manifest = res?.manifest || null;
        serverChecksum = res?.manifest_checksum || '';
    } catch (_) { manifest = null; }

    const existingSurveys    = new Set(manifest?.survey_ids || []);
    const existingRecipients = new Set(manifest?.recipient_ids || []);
    const existingResults    = new Set(manifest?.deployment_ids || []);

    // Resolve display names from the manifest's contents catalog so the dialog
    // can name artifacts rather than surface UUIDs.
    const nameByID = {};
    (manifest?.contents || []).forEach(c => { if (c && c.id) nameByID[c.id] = c.name || c.id; });

    const collisions = [];
    (selector.survey_ids || []).forEach(id => {
        if (existingSurveys.has(id)) collisions.push({ id, type: 'survey', name: nameByID[id] || id });
    });
    (selector.recipient_ids || []).forEach(id => {
        if (existingRecipients.has(id)) collisions.push({ id, type: 'recipient', name: nameByID[id] || id });
    });
    (selector.deployment_ids || []).forEach(id => {
        if (existingResults.has(id)) collisions.push({ id, type: 'result', name: nameByID[id] || id });
    });

    // No overwrite, or no gate supplied — go straight through.
    if (collisions.length === 0 || typeof confirmOverwrite !== 'function') {
        return archiveSelectiveWithResync(selector, onProgress, confirmResync);
    }

    // 2. Build the remembered-consent key from the current archive state + the
    //    selection, so approval does not leak into a different archive/selection.
    const selectionIDs = [
        ...(selector.survey_ids || []),
        ...(selector.recipient_ids || []),
        ...(selector.deployment_ids || []),
    ].slice().sort();

    // Key by the canonical state (server checksum), not local bytes — the
    // canonical copy is what an overwrite replaces.
    const ssKey = `overwriteOK:${serverChecksum}:${selectionIDs.join(',')}`;

    let approved = false;
    try { approved = sessionStorage.getItem(ssKey) === '1'; } catch (_) {}

    if (!approved) {
        const ok = await confirmOverwrite(collisions, getActiveArchiveFileName());
        if (!ok) {
            const err = new Error('Overwrite declined by author');
            err.canceled = true;
            throw err;
        }
        try { sessionStorage.setItem(ssKey, '1'); } catch (_) {}
    }

    // 3. Run the archive. Clear the remembered consent on success so a future
    //    re-archive prompts again; keep it on failure so an in-batch retry of
    //    the same selection/state does not re-prompt (spec §4g).
    const res = await archiveSelectiveWithResync(selector, onProgress, confirmResync);
    try { sessionStorage.removeItem(ssKey); } catch (_) {}
    return res;
}

// ---- Session channel (console = the one local writer) ----

let _channel = null;

/**
 * Open the session channel to the archive server and keep the local file
 * current. Call when an archive file is open; the subscription lives for the
 * console session (App.js owns start/stop). Each announcement — including the
 * "hello" on every (re)connect — triggers an idempotent write-back: the
 * writer compares checksums and only writes when the local file is behind,
 * so replays, reconnect gaps, and the submitting tab's own inline write-back
 * all coexist safely.
 *
 * @param {object} [handlers]
 * @param {(result: {written: boolean, checksum?: string, event: object}) => void} [handlers.onWriteBack]
 * @param {(state: 'connected'|'reconnecting'|'closed') => void} [handlers.onStatus]
 * @param {(err: Error, event: object) => void} [handlers.onError]
 */
export function startArchiveChannel(handlers = {}) {
    stopArchiveChannel();
    const { onWriteBack, onStatus, onError } = handlers;
    _channel = openArchiveChannel({
        onStatus,
        onEvent: async (ev) => {
            // No checksum means the server has no live scratch copy — a fresh
            // account, or the usual state once the day-old scratch object and
            // its bookkeeping row have expired. Nothing to pull; the file on
            // this machine is the one that matters and it is already correct.
            if (!ev || !ev.manifest_checksum) return;
            try {
                // Hand the announced checksum to the writer so it can skip the
                // download-link request when the local file already matches.
                const result = await writeBackFromServer(undefined, ev.manifest_checksum);
                if (onWriteBack) onWriteBack({ ...result, event: ev });
            } catch (err) {
                // A stale/vanished handle lands here; the UI turns it into the
                // re-open prompt. The merge itself is safe on the server.
                if (onError) onError(err, ev);
            }
        },
    });
    return _channel;
}

export function stopArchiveChannel() {
    if (_channel) {
        try { _channel.close(); } catch (_) { }
        _channel = null;
    }
}

